import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ToastProps {
  id: string;
  title?: string;
  description: string;
  variant?: 'default' | 'destructive';
  onClose: (id: string) => void;
}

export const Toast = ({
  id,
  title,
  description,
  variant = 'default',
  onClose,
}: ToastProps) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [id, onClose]);

  return (
    <div
      className={cn(
        'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border border-border bg-background shadow-lg',
        variant === 'destructive' &&
          'bg-destructive/10 border-destructive/50 dark:border-destructive'
      )}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-1">
            {title && (
              <p
                className={cn(
                  'text-sm font-medium',
                  variant === 'default' ? 'text-foreground' : 'text-destructive'
                )}
              >
                {title}
              </p>
            )}
            <p
              className={cn(
                'mt-1 text-sm',
                variant === 'default'
                  ? 'text-muted-foreground'
                  : 'text-destructive'
              )}
            >
              {description}
            </p>
          </div>
          <div className="ml-4 flex shrink-0">
            <button
              className={cn(
                'inline-flex rounded-md text-muted-foreground hover:text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                variant === 'destructive' &&
                  'text-destructive/70 hover:text-destructive'
              )}
              onClick={() => onClose(id)}
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ToastContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[100] flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        {children}
      </div>
    </div>
  );
};
